/**
 * QuoteResponseModal.jsx
 *
 * Modal para que los proveedores respondan a solicitudes de cotización.
 * Permite ingresar precio, días de entrega y notas.
 */

import { useState, useEffect } from "react"
import {
    Modal,
    Form,
    InputNumber,
    Input,
    Select,
    Button,
    Space,
    Typography,
    Descriptions,
    Divider,
    message,
    Alert,
    Table,
    Tag
} from "antd"
import {
    SendOutlined,
    CloseCircleOutlined,
    DollarOutlined,
    CalendarOutlined,
    ShoppingCartOutlined,
    MailOutlined
} from "@ant-design/icons"
import { respondToQuoteRequest, declineQuoteRequest } from "../../services/QuoteRequestServices"
import { useAuth } from "../../contexts/authContext"

const { Text } = Typography
const { TextArea } = Input
const { Option } = Select

const QuoteResponseModal = ({
    visible,
    onCancel,
    quoteRequest,
    getToken,
    onSuccess
}) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [declining, setDeclining] = useState(false)
    const { currentUser } = useAuth()

    // Obtener datos del draft si existe
    const getDraftData = () => {
        if (!quoteRequest?.responses || !currentUser?.uid) return null
        const myResponse = quoteRequest.responses[currentUser.uid]
        if (myResponse?.status === "draft") {
            return myResponse
        }
        return null
    }

    const draftData = getDraftData()

    // Pre-cargar formulario con datos del draft cuando el modal se abre
    useEffect(() => {
        if (visible && draftData) {
            form.setFieldsValue({
                price: draftData.price || undefined,
                currency: draftData.currency || "ARS",
                deliveryDays: draftData.delivery_days || 7,
                notes: draftData.notes || ""
            })
        } else if (visible) {
            form.setFieldsValue({
                currency: "ARS",
                deliveryDays: 7
            })
        }
    }, [visible, draftData, form])

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            setLoading(true)

            const token = await getToken()

            const productQuotes = []

            if (isCartRequest) {
                for (const product of quoteRequest.cart_products) {
                    const productId = product?.product_id ?? product?.id
                    if (!productId) continue

                    const measures = product?.measures || []
                    const hasMeasures = measures.length > 0

                    if (hasMeasures) {
                        // Procesar cada medida como un item separado
                        const measureQuotes = []
                        let productTotal = 0

                        for (const measure of measures) {
                            const measureKey = `${productId}_${measure.measure}`
                            const rawPrice = values.measurePrices?.[measureKey]
                            const unitPrice = typeof rawPrice === "number" ? rawPrice : Number(rawPrice)
                            const quantity = measure.quantity || 0
                            const subtotal = unitPrice * quantity
                            productTotal += subtotal

                            measureQuotes.push({
                                measure: measure.measure,
                                unit_price: unitPrice,
                                quantity: quantity,
                                subtotal: subtotal
                            })
                        }

                        productQuotes.push({
                            product_id: productId,
                            product_name: product.product_name,
                            measure_quotes: measureQuotes,
                            price: productTotal,
                            currency: values.currency
                        })
                    } else {
                        // Producto sin medidas
                        const productKey = String(productId)
                        const rawPrice = values.productPrices?.[productKey]
                        const unitPrice = typeof rawPrice === "number" ? rawPrice : Number(rawPrice)
                        const quantity = product?.quantity || 1
                        const totalPrice = unitPrice * quantity

                        productQuotes.push({
                            product_id: productId,
                            product_name: product.product_name,
                            unit_price: unitPrice,
                            quantity: quantity,
                            price: totalPrice,
                            currency: values.currency
                        })
                    }
                }
            }

            // Calcular precio total basado en precios unitarios
            const submittedPrice = isCartRequest
                ? productQuotes.reduce((sum, item) => {
                    return sum + (Number.isFinite(item.price) ? item.price : 0)
                }, 0)
                : values.price * (quoteRequest.quantity || 1)

            // Guardar también el precio unitario para solicitudes simples
            const unitPrice = isCartRequest ? null : values.price

            await respondToQuoteRequest(
                token,
                quoteRequest.id,
                submittedPrice,
                values.currency,
                values.deliveryDays,
                values.notes || "",
                [],
                productQuotes,
                unitPrice
            )

            message.success("Cotización enviada exitosamente")
            form.resetFields()
            onSuccess?.()
        } catch (error) {
            if (error.errorFields) {
                // Error de validación del formulario
                return
            }
            console.error("Error sending quote response:", error)
            message.error(error.message || "Error al enviar la cotización")
        } finally {
            setLoading(false)
        }
    }

    const handleDecline = async () => {
        try {
            setDeclining(true)
            const token = await getToken()

            await declineQuoteRequest(token, quoteRequest.id)

            message.info("Solicitud rechazada")
            onSuccess?.()
        } catch (error) {
            console.error("Error declining quote request:", error)
            message.error(error.message || "Error al rechazar la solicitud")
        } finally {
            setDeclining(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        onCancel?.()
    }

    if (!quoteRequest) return null

    // Verificar si es solicitud de carrito
    const isCartRequest = quoteRequest.type === "cart_request" && quoteRequest.cart_products?.length > 0

    const getProductQuantity = (product) => {
        const totalMeasures = product?.measures?.reduce((sum, m) => sum + (m.quantity || 0), 0) || 0
        return totalMeasures || product?.quantity || 0
    }

    const totalQuantity = isCartRequest
        ? quoteRequest.cart_products.reduce((sum, product) => sum + getProductQuantity(product), 0)
        : quoteRequest.quantity || 0

    // Columnas para la tabla de productos del carrito
    const cartProductColumns = [
        {
            title: "Producto",
            dataIndex: "product_name",
            key: "product_name",
            render: (_, record) => {
                const isSimilar =
                    typeof record?.is_similar_product === "boolean"
                        ? record.is_similar_product
                        : record?.requested_product_id
                          ? record.requested_product_id !== record.product_id
                          : record?.requested_product_name &&
                            record?.requested_product_name !== record?.product_name
                return (
                    <div>
                        <Text strong>{record.product_name}</Text>
                        {isSimilar && (
                            <Tag color="blue" style={{ marginInlineStart: 6 }}>
                                Similar
                            </Tag>
                        )}
                        {record.requested_product_name &&
                            record.requested_product_name !== record.product_name && (
                                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                                    Solicitado: {record.requested_product_name}
                                </Text>
                            )}
                    </div>
                )
            }
        },
        {
            title: "Medidas y Cantidades",
            dataIndex: "measures",
            key: "measures",
            render: (measures) => (
                <div>
                    {measures?.map((m, idx) => (
                        <Tag key={idx} color="blue" style={{ margin: 2 }}>
                            {m.measure}: {m.quantity} uds
                        </Tag>
                    )) || "-"}
                </div>
            )
        },
        {
            title: "Total",
            key: "total",
            width: 80,
            align: "center",
            render: (_, record) => {
                return <Text strong>{getProductQuantity(record)}</Text>
            }
        }
    ]

    return (
        <Modal
            title={draftData ? "Revisar y Confirmar Cotización" : "Responder Solicitud de Cotización"}
            open={visible}
            onCancel={handleCancel}
            width={700}
            footer={null}
            destroyOnClose
        >
            {/* Alerta de borrador si viene de email */}
            {draftData && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<MailOutlined />}
                    message="Cotización pre-completada desde email"
                    description="Hemos extraído los datos de tu cotización enviada por email. Por favor revisa que la información sea correcta y confirma para enviarla al comprador."
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* Información de la solicitud */}
            <Alert
                type="info"
                showIcon
                icon={isCartRequest ? <ShoppingCartOutlined /> : undefined}
                message={
                    isCartRequest
                        ? `Un comprador solicita cotización para ${quoteRequest.cart_products.length} producto(s)`
                        : "Un comprador está solicitando cotización para el siguiente producto"
                }
                style={{ marginBottom: 16 }}
            />

            {/* Información del comprador */}
            <Descriptions
                column={2}
                size="small"
                style={{ marginBottom: 16 }}
            >
                <Descriptions.Item label="Comprador">
                    <Text strong>{quoteRequest.buyer_name || "N/A"}</Text>
                </Descriptions.Item>
                {quoteRequest.buyer_company && (
                    <Descriptions.Item label="Empresa">
                        {quoteRequest.buyer_company}
                    </Descriptions.Item>
                )}
            </Descriptions>

            {/* Si es solicitud de carrito, mostrar tabla de productos */}
            {isCartRequest ? (
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                        Productos solicitados:
                    </Text>
                    <Table
                        dataSource={quoteRequest.cart_products}
                        columns={cartProductColumns}
                        rowKey="product_id"
                        size="small"
                        pagination={false}
                        bordered
                    />
                    <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                        Cantidad total solicitada: <Text strong>{totalQuantity}</Text> unidades
                    </Text>
                </div>
            ) : (
                <Descriptions
                    column={1}
                    bordered
                    size="small"
                    style={{ marginBottom: 24 }}
                >
                    <Descriptions.Item label="Producto de referencia">
                        <Text strong>{quoteRequest.original_product_name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Categoría">
                        {quoteRequest.original_product_category}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cantidad solicitada">
                        <Text strong>{quoteRequest.quantity}</Text> unidades
                    </Descriptions.Item>
                </Descriptions>
            )}

            {/* Especificaciones y notas */}
            {(quoteRequest.specifications || quoteRequest.notes || quoteRequest.delivery_deadline) && (
                <Descriptions
                    column={1}
                    bordered
                    size="small"
                    style={{ marginBottom: 24 }}
                >
                    {quoteRequest.specifications && (
                        <Descriptions.Item label="Especificaciones">
                            {quoteRequest.specifications}
                        </Descriptions.Item>
                    )}
                    {quoteRequest.delivery_deadline && (
                        <Descriptions.Item label="Fecha límite de entrega">
                            {new Date(quoteRequest.delivery_deadline).toLocaleDateString("es-AR")}
                        </Descriptions.Item>
                    )}
                    {quoteRequest.notes && (
                        <Descriptions.Item label="Notas del comprador">
                            {quoteRequest.notes}
                        </Descriptions.Item>
                    )}
                </Descriptions>
            )}

            <Divider>Tu Cotización</Divider>

            <Form
                form={form}
                layout="vertical"
            >
                {isCartRequest ? (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ display: "block", marginBottom: 8 }}>
                            Precio unitario por producto y medida
                        </Text>
                        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                            Ingresa el precio por unidad de cada producto/medida. El total se calculará automáticamente.
                        </Text>
                        {quoteRequest.cart_products.map((product, productIndex) => {
                            const productId = product?.product_id ?? product?.id
                            const measures = product?.measures || []
                            const hasMeasures = measures.length > 0

                            if (hasMeasures) {
                                // Si tiene medidas, mostrar un input por cada medida
                                return (
                                    <div key={productId || productIndex} style={{ marginBottom: 16, padding: 12, backgroundColor: "#fafafa", borderRadius: 8 }}>
                                        <Text strong style={{ display: "block", marginBottom: 12 }}>
                                            {product.product_name}
                                        </Text>
                                        {measures.map((measure, measureIndex) => {
                                            const measureKey = `${productId}_${measure.measure}`
                                            return (
                                                <Form.Item
                                                    key={measureKey}
                                                    name={["measurePrices", measureKey]}
                                                    label={`Medida: ${measure.measure} (${measure.quantity} uds)`}
                                                    rules={[
                                                        { required: true, message: "Ingresa el precio unitario" },
                                                        { type: "number", min: 0.01, message: "El precio debe ser mayor a 0" }
                                                    ]}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <InputNumber
                                                        style={{ width: "100%" }}
                                                        placeholder={`Precio por unidad (${measure.measure})`}
                                                        prefix={<DollarOutlined />}
                                                        formatter={(value) =>
                                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                                        }
                                                        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                                                    />
                                                </Form.Item>
                                            )
                                        })}
                                    </div>
                                )
                            } else {
                                // Sin medidas, un solo input para el producto
                                const productKey = String(productId || productIndex)
                                const productQuantity = product?.quantity || 1
                                return (
                                    <Form.Item
                                        key={productKey}
                                        name={["productPrices", productKey]}
                                        label={`${product.product_name} — Precio por unidad (${productQuantity} uds solicitadas)`}
                                        rules={[
                                            { required: true, message: "Ingresa el precio unitario" },
                                            { type: "number", min: 0.01, message: "El precio debe ser mayor a 0" }
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: "100%" }}
                                            placeholder="Ej: 1500 (precio por unidad)"
                                            prefix={<DollarOutlined />}
                                            formatter={(value) =>
                                                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }
                                            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                                        />
                                    </Form.Item>
                                )
                            }
                        })}
                    </div>
                ) : (
                    <Form.Item
                        name="price"
                        label={`Precio Unitario (${quoteRequest.quantity || 1} unidades solicitadas)`}
                        rules={[
                            { required: true, message: "Ingresa el precio unitario" },
                            { type: "number", min: 0.01, message: "El precio debe ser mayor a 0" }
                        ]}
                        extra="Ingresa el precio por unidad. El total se calculará automáticamente."
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Ej: 1500 (precio por unidad)"
                            prefix={<DollarOutlined />}
                            formatter={(value) =>
                                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                            }
                            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                        />
                    </Form.Item>
                )}

                <Form.Item
                    name="currency"
                    label="Moneda"
                    rules={[{ required: true, message: "Selecciona la moneda" }]}
                >
                    <Select>
                        <Option value="ARS">ARS - Peso Argentino</Option>
                        <Option value="USD">USD - Dólar Estadounidense</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="deliveryDays"
                    label="Días de Entrega Estimados"
                    rules={[
                        { required: true, message: "Ingresa los días de entrega" },
                        { type: "number", min: 1, message: "Mínimo 1 día" }
                    ]}
                >
                    <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Ej: 7"
                        prefix={<CalendarOutlined />}
                        addonAfter="días"
                    />
                </Form.Item>

                <Form.Item
                    name="notes"
                    label="Notas (opcional)"
                >
                    <TextArea
                        rows={3}
                        placeholder="Agrega información adicional sobre tu cotización, condiciones de pago, etc."
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Form>

            <Divider />

            <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={handleDecline}
                    loading={declining}
                    disabled={loading}
                >
                    Rechazar Solicitud
                </Button>
                <Space>
                    <Button onClick={handleCancel} disabled={loading || declining}>
                        Cancelar
                    </Button>
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={declining}
                    >
                        {draftData ? "Confirmar Cotización" : "Enviar Cotización"}
                    </Button>
                </Space>
            </Space>
        </Modal>
    )
}

export { QuoteResponseModal }
